import { setupVideoPlayerModal } from '@/modules/setupVideoPlayerModal';
import { rememberPreparedCampaignForDisplay } from '@/helpers/preparedCampaignDisplay';
import { trackStoreeelEvent } from "@/helpers/trackStoreeelEvent";
import localStorageRepositories from "@/plugins/localStorageRepositories";
import { preventFullscreen } from '@/modules/actions/preventFullscreen';
import { applyVideoDownloadSafeguards } from '@/helpers/preventVideoDownload';
import { isReelLike } from '@/helpers/isReelLike';
import {
  normalizeTargetingAnswers,
  prepareCampaignForDisplay,
  rememberStickyInitialAdAssignment,
  restoreTargetingAnswersFromStorage,
} from '@/helpers/streamsTargeting';
import { queryStoreeelAll } from '@/helpers/storeeelShadowDom';

export const campaignIconsTapAction = (root = null) => {
  queryStoreeelAll('.storeeel_campaign', root).forEach(icon => {
    // 戻る操作や再描画で同じDOMが残っている場合があるため、多重バインドを防止する
    if (icon.dataset.storeeelIconBound === 'true') return;
    // アイコン内の動画があれば、iPhone時に全画面抑制を適用しコントロールを削除
    const videoEl = icon.querySelector('video');
    if (videoEl) {
      applyVideoDownloadSafeguards(videoEl);
      if (window.isIphone) {
        preventFullscreen(videoEl);
        videoEl.removeAttribute('controls');
      }
    }
    icon.addEventListener('click', handleIconClick);
    // 一度イベントを付与したらフラグを立てる
    icon.dataset.storeeelIconBound = 'true';
  });
};

function handleIconClick(event) {
  const icon = event.currentTarget;
  const originalCampaign = getCampaignFromIcon(icon);
  if (!originalCampaign) return;

  // イベント計測とローカルストレージ
  recordCampaignClick(originalCampaign);
  recordGroupClick(originalCampaign, icon);

  const tenantId = window.storeeelTenantId || 'default';
  const currentUrl = window.storeeelCurrentUrl || window.location.href;
  const savedAnswers = restoreTargetingAnswersFromStorage(originalCampaign, {
    segments: localStorageRepositories.get('targetingSegment')?.get?.(tenantId) || {},
  });
  const selectedGroupId = getSelectedGroupId(icon);
  // クリック前に見えているサムネイルと初回表示内容を合わせるため、描画済み広告を先に固定する。
  rememberDisplayedInitialAdAssignment(originalCampaign, selectedGroupId, savedAnswers, {
    tenantId,
    currentUrl,
  });
  const preparedCampaign = getPreparedCampaignForOpen(originalCampaign, savedAnswers)
    || prepareCampaignForDisplay(originalCampaign, savedAnswers, {
      tenantId,
      currentUrl,
      // 途中のグループから見始めても広告が届くよう、開いたグループを起点にランダム広告を割り当てる
      startGroupId: selectedGroupId,
    });
  rememberPreparedCampaignForDisplay(originalCampaign, preparedCampaign, savedAnswers);
  const selectedGroup = preparedCampaign?.contentGroups?.find((contentGroup) => String(contentGroup?.id) === selectedGroupId);

  if (!selectedGroup) return;

  const openedContent = selectedGroup?.contents?.[0] || null;
  if (openedContent?.mediaAsset?.type === 'adMediaAsset') {
    // クリック直後に開いた広告は、アンケート回答後も同じ枠で再利用する。
    rememberStickyInitialAdAssignment(originalCampaign, savedAnswers, {
      groupId: selectedGroup.id,
      contentId: openedContent.id,
      mediaAssetId: openedContent.mediaAsset.id,
    }, {
      tenantId,
      currentUrl,
    });
  }

  // アンケート表示前後で再利用する元データを保持する
  window.storeeelOriginalItems = originalCampaign;
  window.storeeelTargetingAnswers = savedAnswers;
  window.storeeelItems = preparedCampaign;
  window.iconId = icon.id;
  window.moviePosition = calcMoviePosition(preparedCampaign, icon);

  const shareState = window.__storeeelShareOpen;
  const shareCampaignId = shareState?.params?.campaignId ?? null;
  if (shareState && (!shareCampaignId || String(shareCampaignId) === String(preparedCampaign.id))) {
    const sharedGroupId = shareState?.iconId ?? shareState?.params?.groupId ?? null;
    if (!sharedGroupId || String(sharedGroupId) === selectedGroupId) {
      // 連携配信の共有リンクは、展開後のどの位置に商品が入るかが抽選結果に依存するため、
      // openSharedContent 側では位置を確定できない（0を仮置きしている）。展開済みグループから引き直す。
      // 先頭(0)や不一致のときは calcMoviePosition の結果を尊重する。PCのリール型・タイル型は
      // moviePosition が「グループの位置」を指す（1グループ1商品なので常に0になる）ため、
      // ここで0に上書きするとクリックしたグループへ飛べなくなる。
      const feedTargetIndex = shareState?.params?.feedProductId && shareState?.targetContentId
        ? selectedGroup?.contents?.findIndex((content) => String(content?.id) === String(shareState.targetContentId))
        : -1;

      if (Number.isFinite(feedTargetIndex) && feedTargetIndex > 0) {
        window.moviePosition = feedTargetIndex;
      } else if (!shareState?.params?.feedProductId && Number.isFinite(shareState?.moviePosition)) {
        window.moviePosition = shareState.moviePosition;
      }
    }
  }

  setupVideoPlayerModal();
}

const cloneCampaign = (campaign) => {
  if (typeof globalThis.structuredClone === 'function') {
    return globalThis.structuredClone(campaign);
  }
  return JSON.parse(JSON.stringify(campaign));
};

const getPreparedCampaignForOpen = (campaign, savedAnswers) => {
  // リール型・タイル型はキャッシュを再利用する
  if (!isReelLike(campaign?.frame)) return null;

  const preparedCampaign = campaign?.__storeeelPreparedCampaignForDisplay;
  if (!preparedCampaign) return null;

  const preparedAnswers = campaign?.__storeeelPreparedCampaignForDisplayAnswers;
  const currentAnswers = JSON.stringify(normalizeTargetingAnswers(savedAnswers));
  if (preparedAnswers !== currentAnswers) return null;

  // モーダル側で window.storeeelItems を扱うため、描画キャッシュは都度複製して使う
  return cloneCampaign(preparedCampaign);
};

const rememberDisplayedInitialAdAssignment = (campaign, selectedGroupId, savedAnswers, options = {}) => {
  const displayedCampaign = campaign?.__storeeelPreparedCampaignForDisplay;
  if (!displayedCampaign || !selectedGroupId) return;
  const displayedAnswers = campaign?.__storeeelPreparedCampaignForDisplayAnswers;
  const currentAnswers = JSON.stringify(normalizeTargetingAnswers(savedAnswers));
  if (!displayedAnswers || displayedAnswers === currentAnswers) return;

  const displayedGroup = displayedCampaign?.contentGroups?.find((contentGroup) => String(contentGroup?.id) === String(selectedGroupId));
  const displayedContent = displayedGroup?.contents?.[0] || null;
  if (displayedContent?.mediaAsset?.type !== 'adMediaAsset') return;

  rememberStickyInitialAdAssignment(campaign, savedAnswers, {
    groupId: displayedGroup.id,
    contentId: displayedContent.id,
    mediaAssetId: displayedContent.mediaAsset.id,
  }, options);
};

// クリックしたアイコンのclassからキャンペーン情報を取得
function getCampaignFromIcon(icon) {
  const id = icon.className.match(/campaign_([^\s]+)/)?.[1];
  return Array.isArray(window.storeeelData)
    ? window.storeeelData.find(item => String(item.id) === String(id))
    : null;
}

// クリックしたグループの位置を取得
function calcMoviePosition(campaign, icon) {
  // リール型・タイル型（PC）ではクリックしたグループの位置を初期スライドとして使う
  if (isReelLike(campaign.frame) && !campaign.isMobile) {
    const groupId = getSelectedGroupId(icon);
    return campaign.contentGroups.findIndex(g => g.id == groupId);
  }
  return 0;
}

// アイコンのDOM idは `storeeel-icon-${contentGroup.id}`（icons.js / preparedCampaignDisplay.js）。
// フィードフォルダの仮想グループは `feed-folder-group-{contentId}-{feedProductId}-{index}` という
// ハイフンを含むidになるため、`split('-').pop()` では末尾の連番しか取れずグループを特定できない。
// preparedCampaignDisplay.js と同じくプレフィックスを除去する方式に揃える。
export const getSelectedGroupId = (icon) => String(icon?.id || '').replace(/^storeeel-icon-/, '');

// クリックしたキャンペーンの情報をローカルストレージに保存とイベント送信
function recordCampaignClick(campaign) {
  trackStoreeelEvent('storeeel_campaign_click', {
    storeeel_name: campaign.name,
    campaign_id:   campaign.id,
  });
  try {
    localStorageRepositories
      .get('campaignClick')
      .set(campaign.id);
  } catch (e) {
    console.warn('localStorageのcampaignClickの保存に失敗:', e);
  }
}

// クリックしたグループの情報をローカルストレージに保存とイベント送信(ストーリー型のみ)
function recordGroupClick(campaign, icon) {
  // リール型・タイル型はグループクリック計測を行わない
  if (isReelLike(campaign.frame)) return;
  const groupId = icon.id.split('-').pop();
  trackStoreeelEvent('storeeel_group_click', {
    storeeel_name: icon.getAttribute('group-name'),
    group_id:      groupId,
    campaign_id:   campaign.id,
  });
  try {
    localStorageRepositories
      .get('groupClick')
      .set(groupId, campaign.id);
  } catch (e) {
    console.warn('localStorageのgroupClickの保存に失敗:', e);
  }
}
